import { usePageMeta } from "@/lib/seo";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Globe2, Video, BookOpen, Sparkles, ShieldCheck } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.8, ease: [0.2, 0.7, 0.3, 1] },
};

const CTA_TO = "/bookings?service=beyond-mindset-masterclass";

function ReserveButton({ testid, subtle = false }) {
  return (
    <div className={subtle ? "" : "flex flex-col items-start gap-3"}>
      <Link
        to={CTA_TO}
        data-testid={testid}
        className="inline-flex items-center gap-3 rounded-full bg-[#a35a38] hover:bg-[#8c4a2d] text-[#faf5ec] px-8 py-4 text-sm md:text-base font-semibold tracking-wide shadow-lg shadow-[#a35a38]/30 transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
      >
        Reserve My Worldwide Seat Now <ArrowRight size={17} />
      </Link>
      <div className="pl-1">
        <span className="font-serif text-lg text-[#7a4a2e]">$47 USD · ₹3,900 INR</span>
        <span className="block text-[11px] uppercase tracking-[0.2em] text-[#9c7a54] mt-1">
          Automated time-zone sync active
        </span>
      </div>
    </div>
  );
}

export default function Masterclass() {
  usePageMeta(
    "Beyond Mindset · Live Masterclass — Just be you by Mehjabin",
    "A premium live masterclass blending modern somatic trauma-release with ancestral Vedic psychology — dissolve burnout, clear intergenerational blockages, restore your nervous system."
  );

  return (
    <div data-testid="masterclass-page" className="bg-[#f3ecdf] text-[#2a2118]">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="masterclass-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#efe5d2] via-[#f3ecdf] to-[#f3ecdf]" />
        <div className="absolute -top-24 -right-24 w-[540px] h-[540px] rounded-full border border-[#d4a574]/30" />
        <div className="absolute top-24 -right-8 w-[380px] h-[380px] rounded-full border border-[#d4a574]/20" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#a35a38]/10 border border-[#a35a38]/25 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#a35a38] mb-8" data-testid="masterclass-badge">
              <Sparkles size={12} /> Live Masterclass · Women's Transformational Work
            </div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight">
              Beyond Mindset: <span className="italic text-[#a35a38]">How Exhausted, High-Achieving Women</span> are Expanding Their Emotional Capacity and Reclaiming Inner Peace.
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-[#4e4032] leading-relaxed">
              A premium live masterclass blending modern somatic trauma-release with timeless, ancestral Vedic
              psychology to safely dissolve burnout, clear intergenerational blockages, and restore your nervous
              system from the inside out.
            </p>
            <div className="mt-10">
              <ReserveButton testid="masterclass-hero-cta" />
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="relative max-w-[420px] mx-auto">
              <div className="absolute -inset-4 rounded-[2.5rem] bg-[#d4a574]/25 blur-2xl" />
              <div className="relative overflow-hidden rounded-[2.5rem] border border-[#d4a574]/40 shadow-2xl shadow-[#7a4a2e]/20">
                <img src="/images/masterclass-hero.jpg" alt="A woman in grounded stillness" className="w-full object-cover" data-testid="masterclass-hero-image" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* EMOTIONAL HOOK */}
      <section className="relative py-20 md:py-28 bg-[#2a2118] text-[#f3ecdf] overflow-hidden" data-testid="masterclass-hook">
        <div className="absolute top-16 right-16 w-56 h-56 rounded-full border border-[#d4a574]/25" />
        <div className="relative max-w-3xl mx-auto px-6 md:px-10">
          <motion.h2 {...fadeUp} className="font-serif font-light text-3xl md:text-5xl leading-snug">
            Does your life look successful on paper, but feel <span className="italic text-[#e0b184]">completely suffocating</span> on the inside?
          </motion.h2>
          <motion.div {...fadeUp} className="mt-10 space-y-6 text-[#f3ecdf]/80 leading-relaxed text-base md:text-lg">
            <p>
              You've read the self-help books. You've tried 'positive thinking' and rigid daily planners. Yet, you
              still wake up with a familiar tightness in your chest, carrying an invisible weight that isn't even
              entirely yours to bear.
            </p>
            <p className="font-serif italic text-xl md:text-2xl text-[#e0b184] border-l-2 border-[#d4a574] pl-6 py-1">
              Here is the truth your mind is trying to protect you from: you cannot think your way out of a
              dysregulated nervous system.
            </p>
            <p>
              When you constantly manage family responsibilities, career shifts, and external expectations, your body
              stores the static. Your emotional capacity shrinks. You aren't failing — your internal container is
              simply full.
            </p>
            <p>
              True transformation doesn't happen by trying to 'fix' your flaws. It happens when you step into the
              depths of your body, look at the shadow, and build the somatic capacity to hold your own power.
            </p>
          </motion.div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-6 md:px-12" data-testid="masterclass-method">
        <motion.div {...fadeUp} className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#a35a38] mb-5">The Methodology</div>
          <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight">
            The Soma-Vedic <span className="italic text-[#a35a38]">Capacity Method</span>
          </h2>
          <p className="mt-6 text-[#4e4032] text-base md:text-lg leading-relaxed">
            We don't just scratch the surface. In this live group masterclass, we use a structured three-pillar
            framework rooted in ancestral wisdom and psychological precision:
          </p>
        </motion.div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-7">
          {[
            {
              n: "01",
              title: "Ancestral Cord Dissolution",
              sub: "Prana Cleansing",
              desc: "Locating and untangling the emotional expectations and heavy family projections you inherited, freeing your energetic body from ancestral guilt.",
            },
            {
              n: "02",
              title: "Subconscious Shadow Cartography",
              sub: "Precision Tracking",
              desc: "Using precise, analytical tracking to find exactly where deep-seated anxieties and micro-traumas are physically trapped in your organs and muscles.",
            },
            {
              n: "03",
              title: "Somatic Expansion Mastery",
              sub: "Vedic Breath & Movement",
              desc: "Gentle, experiential breath and movement tools anchored in traditional Vedic science to widen your nervous system's capacity — allowing you to hold massive success and deep personal boundaries without burning out.",
            },
          ].map((p, i) => (
            <motion.div
              key={p.n}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.12 }}
              className="relative rounded-[2rem] bg-[#ede2cd] border border-[#d4a574]/35 p-8 md:p-9"
              data-testid={`masterclass-pillar-${p.n}`}
            >
              <div className="font-serif text-5xl text-[#d4a574]">{p.n}</div>
              <h3 className="mt-5 font-serif text-2xl leading-snug">{p.title}</h3>
              <div className="mt-1 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#a35a38]">{p.sub}</div>
              <p className="mt-5 text-sm md:text-[15px] text-[#4e4032] leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* LOGISTICS */}
      <section className="py-16 md:py-20 bg-[#ede2cd]/70 border-y border-[#d4a574]/25" data-testid="masterclass-logistics">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { icon: Video, title: "Format", desc: "Live interactive Zoom Sanctuary — replay provided for all global time zones." },
            { icon: BookOpen, title: "Inclusions", desc: "Live somatic clearing session, Q&A integration circle, and your digital Workbook." },
            { icon: ShieldCheck, title: "Friction-Free Checkout", desc: "Secure your slot globally. We accept all international credit cards, PayPal, and domestic payment options." },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex gap-5" data-testid={`masterclass-logistic-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Icon size={26} strokeWidth={1.4} className="text-[#a35a38] shrink-0 mt-1" />
              <div>
                <h3 className="font-serif text-xl">{title}</h3>
                <p className="mt-2 text-sm text-[#4e4032] leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WORKSHOP */}
      <section className="py-20 md:py-28 max-w-6xl mx-auto px-6 md:px-12" data-testid="masterclass-workshop-section">
        <motion.div {...fadeUp} className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center rounded-[2.5rem] bg-[#faf5ec] border border-[#d4a574]/30 p-8 md:p-12">
          <div className="md:col-span-4">
            <div className="overflow-hidden rounded-[1.8rem]">
              <img src="/images/workshop.jpg" alt="Women's Transformational Workshop" className="w-full aspect-[4/5] object-cover" />
            </div>
          </div>
          <div className="md:col-span-8">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#a35a38] mb-4">Also inside the circle</div>
            <h2 className="font-serif font-light text-3xl md:text-4xl leading-tight">
              Women's Transformational Workshop · <span className="italic text-[#a35a38]">Manifestation</span>
            </h2>
            <p className="mt-5 text-[#4e4032] leading-relaxed text-sm md:text-base">
              A live transformational circle for women — the mechanics of becoming a magnet: belief work, scripting,
              and embodiment you can feel in your body. Small, warm, practical — an intimate group of 8. Bring a
              journal and an honest heart.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-5">
              <Link
                to="/bookings?service=manifestation-workshop"
                data-testid="workshop-reserve-btn"
                className="inline-flex items-center gap-2.5 rounded-full border-2 border-[#a35a38] text-[#a35a38] hover:bg-[#a35a38] hover:text-[#faf5ec] px-7 py-3 text-sm font-semibold tracking-wide transition-all duration-300"
              >
                Join the workshop <ArrowRight size={15} />
              </Link>
              <span className="font-serif text-lg text-[#7a4a2e]">₹2,499 · $30</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 md:py-32 bg-[#2a2118] text-[#f3ecdf] overflow-hidden" data-testid="masterclass-final-cta">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] h-[560px] rounded-full border border-[#d4a574]/20" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-[#d4a574]/15" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <Globe2 size={30} strokeWidth={1.3} className="mx-auto text-[#d4a574] mb-8" />
          <h2 className="font-serif font-light text-3xl md:text-5xl leading-snug">
            Your capacity is not fixed.<br />
            <span className="italic text-[#e0b184]">It is waiting to be widened.</span>
          </h2>
          <div className="mt-12 flex flex-col items-center gap-4">
            <Link
              to={CTA_TO}
              data-testid="masterclass-final-cta-btn"
              className="inline-flex items-center gap-3 rounded-full bg-[#d4a574] hover:bg-[#e0b184] text-[#2a2118] px-9 py-4 text-sm md:text-base font-semibold tracking-wide shadow-lg shadow-black/30 transition-all duration-300 hover:-translate-y-0.5"
            >
              Reserve My Worldwide Seat Now <ArrowRight size={17} />
            </Link>
            <span className="font-serif text-lg text-[#e0b184]">$47 USD · ₹3,900 INR</span>
            <span className="text-[11px] uppercase tracking-[0.2em] text-[#f3ecdf]/50">Automated time-zone sync active · replay for every time zone</span>
          </div>
        </div>
      </section>
    </div>
  );
}
